import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Dialog,
  DialogTitle,
  Grid,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Trans, useTranslation } from 'react-i18next';
import { useGetInterviewQuestions } from 'src/api/hooks/useInterview';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import { Input } from 'src/components/Input';
import { Typography } from 'src/components/Typography';
import { TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
import { InterviewQuestion } from 'src/DTO/Job.type';
import { cleanInterviewQuestions } from 'src/utils/interviewQuestionUtils';

const NEW_INTERVIEW_QUESTION: InterviewQuestion = {
  question: '',
  partDuration: 30,
  numOfRetakes: 1,
};

const MAX_PART_DURATION = 100;
const MAX_NUM_OF_RETAKES = 5;

interface InterviewQuestionsProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  interviewQuestions: InterviewQuestion[] | null;
  setInterviewQuestions: (questions: InterviewQuestion[] | null) => void;
}

const InterviewQuestionsDialog = ({
  isOpen,
  setIsOpen,
  interviewQuestions,
  setInterviewQuestions,
}: InterviewQuestionsProps) => {
  const { t } = useTranslation();

  const [questions, setQuestions] = useState<InterviewQuestion[]>(
    interviewQuestions || [NEW_INTERVIEW_QUESTION],
  );
  const [expandedIndex, setExpandedIndex] = useState<number | false>(false);
  const [useDefaultQuestions, setUseDefaultQuestions] = useState(!interviewQuestions?.length);
  const [currentPoint, setCurrentPoint] = useState('');

  const [getInterviewQuestions, { data: defaultQuestions }] = useGetInterviewQuestions();

  const questionsToShow = useDefaultQuestions ? defaultQuestions || [] : questions;

  useEffect(() => {
    getInterviewQuestions();
  }, []);

  const handleChange = (
    value: string | number,
    index: number,
    field: keyof Omit<InterviewQuestion, 'points'>,
  ) => {
    setQuestions(prevQuestions =>
      prevQuestions.map((question, i) =>
        i === index ? { ...question, [field]: value } : question,
      ),
    );
  };

  const handleAddPoint = (point: string) => {
    if (!point.trim() || expandedIndex === false) return;

    setQuestions(prevQuestions =>
      prevQuestions.map((question, i) =>
        i === expandedIndex
          ? { ...question, points: [...(question.points || []), point] }
          : question,
      ),
    );
    setCurrentPoint('');
  };

  const handleDeletePoint = (pointIndex: number) => {
    setQuestions(prevQuestions =>
      prevQuestions.map((question, i) => {
        if (i === expandedIndex) {
          return { ...question, points: question.points?.filter((_, j) => j !== pointIndex) };
        }
        return question;
      }),
    );
  };

  const handleDelete = (index: number) => {
    if (questions.length === 1) {
      setQuestions([NEW_INTERVIEW_QUESTION]);
      return;
    }
    const updatedQuestions = questions.filter((_, i) => i !== index);
    setQuestions(updatedQuestions);
    setExpandedIndex(false);
  };

  const addQuestion = () => {
    if (questions[questions.length - 1]?.question.trim() === '') {
      setExpandedIndex(questions.length - 1);
      return;
    }

    setQuestions([...questions, NEW_INTERVIEW_QUESTION]);
  };

  const handleCheckBoxChange = (checked: boolean) => {
    setUseDefaultQuestions(checked);
    setExpandedIndex(false);
  };

  const handleSave = () => {
    if (useDefaultQuestions) {
      setInterviewQuestions(null);
    } else {
      const cleanedQuestions = cleanInterviewQuestions(questions);
      setInterviewQuestions(cleanedQuestions?.length ? cleanedQuestions : null);
    }
    setIsOpen(false);
    setExpandedIndex(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setQuestions(interviewQuestions || [NEW_INTERVIEW_QUESTION]);
    setUseDefaultQuestions(!interviewQuestions?.length);
    setExpandedIndex(false);
    setCurrentPoint('');
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="lg" onClose={handleClose}>
      <Box sx={{ pb: 2, px: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <DialogTitle>
            <Trans>jobPage.createJobForm.interviewQuestionsDialog.title</Trans>
          </DialogTitle>
          <Tooltip title={t('jobPage.createJobForm.interviewQuestionsDialog.defaultTooltip')}>
            <Box display="flex" alignItems="center" mr={2}>
              <Checkbox
                checked={useDefaultQuestions}
                onChange={e => handleCheckBoxChange(e.target.checked)}
              />
              <Typography fontWeight={TYPOGRAPHY_WEIGHT.BOLD} fontSize="14px">
                <Trans>jobPage.createJobForm.interviewQuestionsDialog.useDefault</Trans>
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        {questionsToShow.map((question, index) => {
          const isExpanded = expandedIndex === index;

          return (
            <Accordion
              key={index}
              sx={{ boxShadow: 'none', '&::before': { display: 'none' } }}
              expanded={isExpanded}
              onChange={() => {
                setCurrentPoint('');
                setExpandedIndex(isExpanded ? false : index);
              }}>
              <AccordionSummary
                expandIcon={<Icon>expand_more</Icon>}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    minWidth: 0,
                  },
                }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                  <Trans
                    i18nKey="jobPage.createJobForm.interviewQuestionsDialog.questionHeading"
                    values={{ number: index + 1, question: !isExpanded ? question.question : '' }}
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Input
                      inputLabel={t('jobPage.createJobForm.interviewQuestionsDialog.question')}
                      value={question.question}
                      multiline
                      disabled={useDefaultQuestions}
                      onChange={e => {
                        const value = e.currentTarget.value;
                        handleChange(value, index, 'question');
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} display="flex" flexDirection="column">
                    <Input
                      inputLabel={t('jobPage.createJobForm.interviewQuestionsDialog.description')}
                      value={question.description || ''}
                      multiline
                      disabled={useDefaultQuestions}
                      onChange={e => {
                        const value = e.currentTarget.value;
                        handleChange(value, index, 'description');
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Input
                      inputLabel={t('jobPage.createJobForm.interviewQuestionsDialog.explanation')}
                      value={question.explanation || ''}
                      multiline
                      disabled={useDefaultQuestions}
                      onChange={e => {
                        const value = e.currentTarget.value;
                        handleChange(value, index, 'explanation');
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Input
                      inputLabel={t('jobPage.createJobForm.interviewQuestionsDialog.conclusion')}
                      value={question.conclusion || ''}
                      multiline
                      disabled={useDefaultQuestions}
                      onChange={e => {
                        const value = e.currentTarget.value;
                        handleChange(value, index, 'conclusion');
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Box flexDirection="row" display="flex" gap={2}>
                      <Box sx={{ flex: 1 }}>
                        <Input
                          inputLabel={t('jobPage.createJobForm.interviewQuestionsDialog.points')}
                          value={currentPoint}
                          disabled={useDefaultQuestions}
                          onChange={e => {
                            const value = e.currentTarget.value;
                            setCurrentPoint(value);
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              handleAddPoint(currentPoint);
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ alignSelf: 'flex-end', mb: 1 }}>
                        <Button
                          onClick={() => handleAddPoint(currentPoint)}
                          disabled={useDefaultQuestions || !currentPoint.trim()}
                          variant={BUTTON_VARIANT.GRADIENT}
                          color={BUTTON_COLOR.DARK}>
                          +
                        </Button>
                      </Box>
                    </Box>
                    {questionsToShow[index].points?.map((point, pointIndex) => (
                      <List
                        key={pointIndex}
                        sx={{ cursor: useDefaultQuestions ? 'default' : 'pointer' }}>
                        <ListItem>
                          <Typography variant="caption" sx={{ mr: 1 }}>
                            {`${pointIndex + 1}.`}
                          </Typography>
                          <Typography variant="caption" sx={{ flex: 1 }}>
                            {point}
                          </Typography>
                          <ListItemIcon>
                            <DeleteIcon
                              sx={{ color: 'dark.main' }}
                              onClick={() => !useDefaultQuestions && handleDeletePoint(pointIndex)}
                            />
                          </ListItemIcon>
                        </ListItem>
                      </List>
                    ))}
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" gap={2}>
                      <Input
                        inputLabel={t(
                          'jobPage.createJobForm.interviewQuestionsDialog.partDuration',
                        )}
                        value={question.partDuration || ''}
                        disabled={useDefaultQuestions}
                        type="number"
                        onChange={e => {
                          const value = Number(e.currentTarget.value);
                          if (isNaN(value) || value < 0) return;
                          const finalValue = value > MAX_PART_DURATION ? MAX_PART_DURATION : value;
                          handleChange(finalValue, index, 'partDuration');
                        }}
                        onBlur={() => {
                          if (!question.partDuration) handleChange(30, index, 'partDuration');
                        }}
                      />

                      <Input
                        inputLabel={t(
                          'jobPage.createJobForm.interviewQuestionsDialog.numOfRetakes',
                        )}
                        value={question.numOfRetakes || ''}
                        disabled={useDefaultQuestions}
                        type="number"
                        onChange={e => {
                          const value = Number(e.currentTarget.value);
                          if (isNaN(value) || value < 0) return;
                          const finalValue =
                            value > MAX_NUM_OF_RETAKES ? MAX_NUM_OF_RETAKES : value;
                          handleChange(finalValue, index, 'numOfRetakes');
                        }}
                        onBlur={() => {
                          if (!question.numOfRetakes) handleChange(1, index, 'numOfRetakes');
                        }}
                      />
                      <Box display="flex" alignItems="end" mb={1}>
                        <Button
                          onClick={() => handleDelete(index)}
                          disabled={useDefaultQuestions}
                          variant={BUTTON_VARIANT.OUTLINED}
                          color={BUTTON_COLOR.DARK}>
                          <DeleteIcon />
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}

        <AccordionActions sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Button onClick={handleClose}>
            <Trans>generic.cancel</Trans>
          </Button>

          <Box gap={2} display="flex">
            {!useDefaultQuestions && (
              <Button
                onClick={addQuestion}
                disabled={useDefaultQuestions}
                variant={BUTTON_VARIANT.GRADIENT}
                color={BUTTON_COLOR.DARK}>
                <Trans>jobPage.createJobForm.interviewQuestionsDialog.addQuestion</Trans>
              </Button>
            )}
            <Button
              disabled={
                useDefaultQuestions
                  ? false
                  : !questionsToShow.length || questionsToShow.some(q => !q.question.trim())
              }
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.DARK}
              onClick={handleSave}>
              <Trans>generic.save</Trans>
            </Button>
          </Box>
        </AccordionActions>
      </Box>
    </Dialog>
  );
};

export default InterviewQuestionsDialog;
